import { NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControlStatus, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { CustomValidators, LegacyDialogsService } from 'cloud-shared-lib';
import * as _ from 'lodash';
import { switchMap, tap } from 'rxjs';
import { DirectivesModule } from '../../directives/directives.module';
import { DisableInputSpinEventsDirective } from '../../directives/disable-input-spin-events.directive';
import { Cutout, CutoutPoint, CutoutTypeEnum } from '../../models/cutting/cutting-panel-outline.model';
import {
  CuttingStandardCutout,
  StandardCutoutAvailablePlates,
} from '../../models/cutting/cutting-standard-cutout.model';
import { Position } from '../../models/cutting/cutting-svg.model';
import { ApiLinks } from '../../modules/cutting-panel-list/csl.api-links';
import { GrooveInfoComponent } from '../../modules/cutting-panel-list/panel-outline-editor-dialog/groove-form/groove-select/groove-info/groove-info.component';
import { ComponentsModule } from '../components.module';
import { ImageViewerComponent, ImageViewerData } from '../image-viewer/image-viewer.component';

@Component({
  selector: 'csl-cutouts-creator',
  templateUrl: 'cutouts-creator.component.html',
  standalone: true,
  imports: [
    MatLegacyTableModule,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    NgIf,
    ReactiveFormsModule,
    MatLegacyTooltipModule,
    MatLegacySelectModule,
    MatLegacyButtonModule,
    ComponentsModule,
    MatIconModule,
    NgFor,
    DisableInputSpinEventsDirective,
    DirectivesModule,
    MatMenuModule,
    GrooveInfoComponent,
  ],
})
export class CutoutsCreatorComponent implements OnChanges {
  thisCutoutTypeEnum = CutoutTypeEnum;
  thisStandardCutoutAvailablePlatesEnum = StandardCutoutAvailablePlates;
  cutoutForm: UntypedFormGroup;
  imageUrl = '';
  rotateDegrees = [90, 0, -90, -180] as const;

  @Input() schema: CuttingStandardCutout;
  @Input() panelHeight: number;
  @Input() panelWidth: number;

  @Output() cutoutChanged: EventEmitter<Cutout> = new EventEmitter();
  @Output() statusChange: EventEmitter<FormControlStatus> = new EventEmitter();

  get showSlotTable(): boolean {
    return !_.isNil(this.schema) && this.schema.slots.length > 0;
  }

  get cutout(): Cutout {
    const cutout = this.cutoutForm.getRawValue() as Cutout;

    if (this.showSlotTable) cutout.slots = this.schema.slots;

    return cutout;
  }

  constructor(private _formBuilder: FormBuilder, private dialogsService: LegacyDialogsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.panelHeight?.currentValue && changes?.panelWidth?.currentValue) {
      this.loadForm();
    }

    if (changes?.schema?.currentValue) {
      this.updateForm();
      this.updateImageUrl();
    }
  }

  cutoutPoint2Position(point: CutoutPoint): Position {
    return point === CutoutPoint.TopLeft
      ? 'tl'
      : point === CutoutPoint.TopRight
      ? 'tr'
      : point === CutoutPoint.BottomRight
      ? 'br'
      : point === CutoutPoint.BottomLeft
      ? 'bl'
      : 'c';
  }

  getError(errors: any): string {
    if (!errors) {
      return '';
    }

    if (errors.range) {
      return $localize`Число должно находиться в диапазоне от ${errors.range.value[0]} до ${errors.range.value[1]}`;
    }

    if (errors.notEqual) {
      return $localize`Поле не должно быть равно ${errors.notEqual.value}`;
    }

    if (errors.required) {
      return $localize`Поле не должно быть пустым`;
    }

    if (errors.number) {
      return $localize`Необходимо ввести число`;
    }

    return '';
  }

  setAngle(pos: Position): void {
    this.cutoutForm.controls.angle.setValue(this.position2CutoutPoint(pos));
  }

  setBase(pos: Position): void {
    this.cutoutForm.controls.basePoint.setValue(this.position2CutoutPoint(pos));
  }

  openImageViewer(link: string): void {
    this.dialogsService.open(ImageViewerComponent, {
      panelClass: 'image-viewer-dialog',
      data: {
        textureLink: link,
      } as ImageViewerData,
    });
  }

  rotateCCW(): void {
    const currentValue = this.cutoutForm.get('rotateDeg').value;
    const currentIndex = this.rotateDegrees.indexOf(currentValue);
    const newIndex = (currentIndex - 1 + this.rotateDegrees.length) % this.rotateDegrees.length;

    this.cutoutForm.get('rotateDeg').setValue(this.rotateDegrees[newIndex]);
  }

  rotateCW(): void {
    const currentValue = this.cutoutForm.get('rotateDeg').value;
    const currentIndex = this.rotateDegrees.indexOf(currentValue);
    const newIndex = (currentIndex + 1) % this.rotateDegrees.length;

    this.cutoutForm.get('rotateDeg').setValue(this.rotateDegrees[newIndex]);
  }

  private updateImageUrl(): void {
    const imageKey = this.schema?.imageS3Key || this.schema?.fragmentImageS3Key;
    this.imageUrl = imageKey ? `${ApiLinks.cuttingStandardCutouts}/image/${imageKey}` : '';
  }

  private position2CutoutPoint(pos: Position): CutoutPoint {
    return pos === 'tl'
      ? CutoutPoint.TopLeft
      : pos === 'tr'
      ? CutoutPoint.TopRight
      : pos === 'br'
      ? CutoutPoint.BottomRight
      : pos === 'bl'
      ? CutoutPoint.BottomLeft
      : CutoutPoint.Center;
  }

  private loadForm(): void {
    this.cutoutForm = this._formBuilder.group({
      name: [''],
      x: [0, [Validators.required, CustomValidators.range([0, this.panelHeight]), CustomValidators.number]],
      y: [0, [Validators.required, CustomValidators.range([0, this.panelWidth]), CustomValidators.number]],
      height: [
        0,
        [
          Validators.required,
          CustomValidators.range([0, this.panelHeight]),
          CustomValidators.number,
          CustomValidators.notEqual(0),
        ],
      ],
      width: [
        0,
        [
          Validators.required,
          CustomValidators.range([0, this.panelWidth]),
          CustomValidators.number,
          CustomValidators.notEqual(0),
        ],
      ],
      radius: [
        { value: 0, disabled: true },
        [Validators.required, CustomValidators.range([0, 500]), CustomValidators.number],
      ],
      rotateDeg: [
        { value: 0, disabled: true },
        [Validators.required, CustomValidators.range([-360, 360]), CustomValidators.number],
      ],
      fileName: [''],
      angle: [CutoutPoint.BottomLeft, [Validators.required]],
      basePoint: [CutoutPoint.BottomLeft, [Validators.required]],
      type: [CutoutTypeEnum.Rectangle, [Validators.required]],
      imageS3Key: [''],
      fragmentImageS3Key: [''],
      fragmentParams: [null],
    });

    this.subscribeToCutoutForm();
  }

  private subscribeToCutoutForm(): void {
    this.cutoutForm.valueChanges
      .pipe(
        tap(v => {
          if (this.cutoutForm.get('type').getRawValue() === CutoutTypeEnum.Fragment) {
            this.cutoutForm.controls.rotateDeg.enable({ emitEvent: false });
          } else {
            this.cutoutForm.controls.rotateDeg.setValue(0, { emitEvent: false });
            this.cutoutForm.controls.rotateDeg.disable({ emitEvent: false });
          }

          if (this.schema?.readonly) return;

          if (v.type === CutoutTypeEnum.Ellipse) {
            this.cutoutForm.controls.width.disable({ emitEvent: false });
          } else {
            this.cutoutForm.controls.width.enable({ emitEvent: false });
            this.cutoutForm.controls.height.enable({ emitEvent: false });
          }

          if (v.type === CutoutTypeEnum.RoundedRectangle) {
            this.cutoutForm.controls.radius.enable({ emitEvent: false });
          } else {
            this.cutoutForm.controls.radius.setValue(0, { emitEvent: false });
            this.cutoutForm.controls.radius.disable({ emitEvent: false });
          }

          if (v.type === CutoutTypeEnum.Ellipse) {
            this.cutoutForm.controls.width.setValue(v.height, { emitEvent: false });
            this.cutoutForm.controls.basePoint.setValue(CutoutPoint.Center, { emitEvent: false });
            this.cutoutForm.controls.width.disable({ emitEvent: false });
          } else {
            this.cutoutForm.controls.width.enable({ emitEvent: false });
          }
        }),
        tap(() => this.cutoutChanged.emit(this.cutout)),
        switchMap(() => this.cutoutForm.statusChanges),
        tap(res => this.statusChange.emit(res))
      )
      .subscribe();
  }

  private updateForm(): void {
    this.cutoutForm.controls.type.setValue(this.schema.type ?? CutoutTypeEnum.Rectangle);

    this.cutoutForm.controls.height.setValue(this.schema.height);
    this.cutoutForm.controls.width.setValue(
      this.schema.type === CutoutTypeEnum.Ellipse ? this.schema.height : this.schema.width
    );

    this.cutoutForm.controls.name.setValue(this.schema.name);
    this.cutoutForm.controls.radius.setValue(this.schema.radius);

    switch (this.schema.type) {
      case CutoutTypeEnum.Ellipse:
        this.cutoutForm.controls.basePoint.setValue(CutoutPoint.Center);
        break;

      case CutoutTypeEnum.Fragment:
        this.cutoutForm.controls.basePoint.setValue(CutoutPoint.Custom);
        break;

      default:
        this.cutoutForm.controls.basePoint.setValue(CutoutPoint.BottomLeft);
        break;
    }

    this.cutoutForm.controls.fileName.setValue(this.schema.fileName);
    this.cutoutForm.controls.fragmentImageS3Key.setValue(this.schema.fragmentImageS3Key ?? '');
    this.cutoutForm.controls.imageS3Key.setValue(this.schema.imageS3Key ?? '');
    this.cutoutForm.controls.fragmentParams.setValue(this.schema.fragmentParams);

    if (this.schema.readonly) {
      this.cutoutForm.controls.height.disable();
      this.cutoutForm.controls.type.disable();
      this.cutoutForm.controls.width.disable();
      this.cutoutForm.controls.radius.disable();
    }

    this.cutoutForm.updateValueAndValidity();
  }
}
